import { useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GET_ADMIN_QR_RISK_LOG_GROUPED_QUERY, GET_ADMIN_QR_RISK_LOG_QUERY } from '@app/constants/query-api-configs';

import { useBasicQueryControls } from '@app/hooks/useBasicQueryControls.hook';

import { Typography } from '@mui/material';

import { Button, Table } from '@app/components';
import { useGroupedTableColumns } from './useGroupedTableColumns';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { EAccountRiskCategory, EQrCodeSourceAction, EWebRiskVerdict } from '@app/swagger-types';
import { oneOfEnum } from '@app/utils/yup.utils';
import { QrCodeRiskLogApi } from '@app/domain/qr-code-risk-log/api/QrCodeRiskLogApi';

interface Props {
  onViewHistory: (qrCodeId: string) => void;
}

export const AdminQrRiskLogGroupedTable: React.FC<Props> = ({ onViewHistory }) => {
  const queryClient = useQueryClient();
  const [togglingQrCodeId, setTogglingQrCodeId] = useState<string | undefined>();
  const [blockingQrCodeId, setBlockingQrCodeId] = useState<string | undefined>();
  const [unblockingQrCodeId, setUnblockingQrCodeId] = useState<string | undefined>();

  const {
    values: { page, size },
    handlers: { handlePageChange, handleSizeChange },
  } = useBasicQueryControls({
    initialValues: { page: 1, size: 10 },
    prefix: GET_ADMIN_QR_RISK_LOG_GROUPED_QUERY.tablePrefix,
  });
  const [wouldBlock] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.wouldBlockParam, withDefault(StringParam, ''));
  const [accountCategory] = useQueryParam(
    GET_ADMIN_QR_RISK_LOG_QUERY.accountCategoryParam,
    withDefault(StringParam, '')
  );
  const [sourceAction] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.sourceActionParam, withDefault(StringParam, ''));
  const [webRiskVerdict] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.webRiskVerdictParam, withDefault(StringParam, ''));
  const [qrCodeId] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.qrCodeIdParam, withDefault(StringParam, ''));
  const [reviewed] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.reviewedParam, withDefault(StringParam, 'false'));

  const queryKeyParams = { page, size, qrCodeId, wouldBlock, accountCategory, sourceAction, webRiskVerdict, reviewed };

  const { data, isFetching, refetch } = useQuery(
    [GET_ADMIN_QR_RISK_LOG_GROUPED_QUERY.name, queryKeyParams],
    () => {
      const validAccountCategory = oneOfEnum(EAccountRiskCategory).isValidSync(accountCategory)
        ? accountCategory
        : undefined;
      const validSourceAction = oneOfEnum(EQrCodeSourceAction).isValidSync(sourceAction) ? sourceAction : undefined;
      const validWebRiskVerdict = oneOfEnum(EWebRiskVerdict).isValidSync(webRiskVerdict) ? webRiskVerdict : undefined;
      return QrCodeRiskLogApi.adminGetGrouped({
        page: Math.ceil((page || 1) - 1),
        size,
        qrCodeId: qrCodeId || undefined,
        wouldBlock: wouldBlock === 'true' ? true : wouldBlock === 'false' ? false : undefined,
        accountCategory: validAccountCategory,
        sourceAction: validSourceAction,
        webRiskVerdict: validWebRiskVerdict,
        reviewed: reviewed === '' ? undefined : reviewed === 'true',
      });
    },
    { keepPreviousData: true, ...GET_ADMIN_QR_RISK_LOG_GROUPED_QUERY.config }
  );

  const toggleReviewedMutation = useMutation({
    mutationFn: ({ code, nextReviewed }: { code: string; nextReviewed: boolean }) =>
      QrCodeRiskLogApi.adminMarkReviewed(code, nextReviewed),
    onMutate: ({ code }: { code: string; nextReviewed: boolean }) => setTogglingQrCodeId(code),
    onSettled: () => {
      setTogglingQrCodeId(undefined);
      queryClient.invalidateQueries(GET_ADMIN_QR_RISK_LOG_GROUPED_QUERY.name);
      queryClient.invalidateQueries(GET_ADMIN_QR_RISK_LOG_QUERY.name);
    },
  });

  const blockMutation = useMutation({
    mutationFn: (code: string) => QrCodeRiskLogApi.adminBlock(code),
    onMutate: (code: string) => setBlockingQrCodeId(code),
    onSettled: () => {
      setBlockingQrCodeId(undefined);
      queryClient.invalidateQueries(GET_ADMIN_QR_RISK_LOG_GROUPED_QUERY.name);
      queryClient.invalidateQueries(GET_ADMIN_QR_RISK_LOG_QUERY.name);
    },
  });

  const unblockMutation = useMutation({
    mutationFn: (code: string) => QrCodeRiskLogApi.adminUnblock(code),
    onMutate: (code: string) => setUnblockingQrCodeId(code),
    onSettled: () => {
      setUnblockingQrCodeId(undefined);
      queryClient.invalidateQueries(GET_ADMIN_QR_RISK_LOG_GROUPED_QUERY.name);
      queryClient.invalidateQueries(GET_ADMIN_QR_RISK_LOG_QUERY.name);
    },
  });

  const columns = useGroupedTableColumns({
    onViewHistory,
    onToggleReviewed: (code, nextReviewed) => toggleReviewedMutation.mutate({ code, nextReviewed }),
    togglingQrCodeId,
    onBlock: (code) => blockMutation.mutate(code),
    blockingQrCodeId,
    onUnblock: (code) => unblockMutation.mutate(code),
    unblockingQrCodeId,
  });

  const leftContent = useMemo(() => {
    return (
      <div>
        <Typography variant="xl" className="font-bold">
          QR risk log ({data?.total})
        </Typography>
      </div>
    );
  }, [data?.total]);

  const rightContent = useMemo(() => {
    return (
      <div>
        <Button loading={isFetching} onClick={() => refetch()}>
          Refetch
        </Button>
      </div>
    );
  }, [refetch, isFetching]);

  return (
    <Table
      idKey="qrCodeId"
      cols={columns}
      tableData={data?.result ?? []}
      leftHeaderContent={leftContent}
      rightHeaderContent={rightContent}
      notFoundMessage="Nothing open here."
      currentPage={page}
      loading={isFetching}
      pageSize={size}
      onSizeChange={handleSizeChange}
      total={data?.total}
      onPagination={handlePageChange}
    />
  );
};
